export const MAX_GALLERY_COUNT = 6;
export const MAX_SOURCE_BYTES = 20 * 1024 * 1024;
export const MAX_OUTPUT_BYTES = 1.5 * 1024 * 1024;
const MAX_DIMENSION = 1600;

const UNSUPPORTED_EXT = ['.heic', '.heif', '.avif', '.gif', '.bmp', '.tiff', '.tif', '.dng'];

export function isUnsupportedGalleryFormat(file: Pick<File, 'name' | 'type'>): boolean {
  const name = (file.name || '').toLowerCase();
  const mime = (file.type || '').toLowerCase();
  if (mime.includes('heic') || mime.includes('heif') || mime.includes('avif')) {
    return true;
  }
  return UNSUPPORTED_EXT.some((ext) => name.endsWith(ext));
}

function canvasToBlob(canvas: HTMLCanvasElement, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('Gagal mengompres foto. Coba pilih foto lain.'));
          return;
        }
        resolve(blob);
      },
      'image/jpeg',
      quality
    );
  });
}

async function loadDrawable(file: File): Promise<{
  width: number;
  height: number;
  draw: (ctx: CanvasRenderingContext2D, width: number, height: number) => void;
  close: () => void;
}> {
  if (typeof createImageBitmap === 'function') {
    try {
      const bitmap = await createImageBitmap(file);
      return {
        width: bitmap.width,
        height: bitmap.height,
        draw: (ctx, width, height) => {
          ctx.drawImage(bitmap, 0, 0, width, height);
        },
        close: () => bitmap.close(),
      };
    } catch {
      // Fallback ke HTMLImageElement untuk WebView yang gagal decode lewat createImageBitmap.
    }
  }

  const objectUrl = URL.createObjectURL(file);
  try {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('FORMAT'));
      img.src = objectUrl;
    });
    return {
      width: image.naturalWidth || image.width,
      height: image.naturalHeight || image.height,
      draw: (ctx, width, height) => {
        ctx.drawImage(image, 0, 0, width, height);
      },
      close: () => URL.revokeObjectURL(objectUrl),
    };
  } catch {
    URL.revokeObjectURL(objectUrl);
    throw new Error('Format foto tidak didukung. Gunakan JPG, PNG, atau WebP.');
  }
}

export async function prepareGalleryFile(file: File): Promise<File> {
  if (!file || file.size === 0) {
    throw new Error('Foto tidak dapat dibaca. Coba pilih ulang dari Galeri.');
  }
  if (file.size > MAX_SOURCE_BYTES) {
    throw new Error('Ukuran foto terlalu besar (maksimal 20MB). Pilih foto lain.');
  }

  try {
    const drawable = await loadDrawable(file);
    try {
      if (drawable.width < 1 || drawable.height < 1) {
        throw new Error('Foto tidak valid. Coba pilih foto lain.');
      }

      let width = drawable.width;
      let height = drawable.height;
      const scale = Math.min(1, MAX_DIMENSION / Math.max(width, height));
      width = Math.max(1, Math.round(width * scale));
      height = Math.max(1, Math.round(height * scale));

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        throw new Error('Gagal menyiapkan foto. Coba lagi.');
      }
      drawable.draw(ctx, width, height);

      let quality = 0.82;
      let blob = await canvasToBlob(canvas, quality);
      while (blob.size > MAX_OUTPUT_BYTES && quality > 0.5) {
        quality -= 0.12;
        blob = await canvasToBlob(canvas, quality);
      }

      if (blob.size > MAX_OUTPUT_BYTES) {
        throw new Error('Foto terlalu besar setelah dikompres. Pilih foto lain.');
      }

      return new File([blob], `gallery-${Date.now()}.jpg`, {
        type: 'image/jpeg',
        lastModified: Date.now(),
      });
    } finally {
      drawable.close();
    }
  } catch (err) {
    if (err instanceof Error) {
      const keepOriginal =
        err.message.startsWith('Gagal') ||
        err.message.startsWith('Foto') ||
        err.message.startsWith('Ukuran');
      if (keepOriginal) {
        throw err;
      }
    }
    if (isUnsupportedGalleryFormat(file)) {
      throw new Error('Format HEIC/AVIF tidak didukung. Simpan sebagai JPG lalu unggah ulang.');
    }
    if (err instanceof Error) {
      throw err;
    }
    throw new Error('Format foto tidak didukung. Gunakan JPG, PNG, atau WebP.');
  }
}

export async function prepareGalleryFiles(
  files: File[],
  currentCount: number
): Promise<{ files: File[]; error?: string }> {
  if (currentCount + files.length > MAX_GALLERY_COUNT) {
    return {
      files: [],
      error: `Maksimal ${MAX_GALLERY_COUNT} foto di galeri`,
    };
  }

  const prepared: File[] = [];
  let firstError: string | undefined;

  for (const file of files) {
    if (currentCount + prepared.length >= MAX_GALLERY_COUNT) {
      break;
    }
    try {
      prepared.push(await prepareGalleryFile(file));
    } catch (err) {
      if (!firstError) {
        firstError = err instanceof Error ? err.message : 'Gagal menyiapkan foto';
      }
    }
  }

  if (prepared.length === 0) {
    return {
      files: [],
      error: firstError || 'Gagal menyiapkan foto. Coba pilih ulang.',
    };
  }

  return { files: prepared, error: firstError };
}
